import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  Plug,
  Search,
  Server,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import {
  CATALOG,
  CUSTOM_MCP_CATALOG_ID,
  GOOGLE_WORKSPACE_CONNECTION_SLUG,
  catalogConfig,
  catalogEntriesFromBackend,
  catalogEntryConnectionSlug,
  catalogEntryFormat,
  catalogEntryIsImport,
  catalogEntryUsesMCPConnectFlow,
  catalogFieldValue,
  catalogSearchMatches,
  catalogSecret,
  customMCPInitialPolicy,
  customMCPManifestFromProbe,
  customMCPToolsFromProbe,
  deriveCustomMCPSlug,
  googleServiceConfiguredForIntegration,
  googleWorkspaceOAuthPayload,
  legacyPolicyState,
  normalizeWorkspaceIntegrationPolicyState,
  policyDraftFromPolicies,
  policyLabelForState,
  requiredCatalogFieldsPresent,
  workspaceIntegrationSnapshotStatus,
} from "./workspaceIntegrationsCatalog";
import type {
  CatalogEntry,
  CatalogFormat,
  OAuthPermissionSelection,
  PermissionLevel,
  PolicyDraft,
} from "./workspaceIntegrationsCatalog";

import { useAuth } from "../lib/auth";
import { COPILOT_DEFAULT_PERMISSIONS_SUMMARY, CopilotPanel } from "../components/integrations/CopilotPanel";
import { ExternalAgentsCard } from "../components/integrations/ExternalAgentsCard";
import type { CopilotQuickAdd } from "../components/integrations/CopilotPanel";
import {
  approveWorkspaceIntegrationApprovalRequest,
  createWorkspaceIntegrationImport,
  createWorkspaceIntegration,
  denyWorkspaceIntegrationApprovalRequest,
  listWorkspaceIntegrationApprovalRequests,
  listWorkspaceIntegrationCatalog,
  listWorkspaceIntegrationHealth,
  listMembers,
  listWorkspaceIntegrations,
  previewWorkspaceIntegrationImport,
  probeWorkspaceIntegration,
  revokeWorkspaceIntegration,
  startWorkspaceIntegrationOAuth,
  testWorkspaceIntegration,
  updateWorkspaceIntegrationPolicy,
} from "../lib/api";
import type {
  WorkspaceIntegration,
  WorkspaceIntegrationApprovalRequest,
  WorkspaceIntegrationLastTest,
  WorkspaceIntegrationCatalogItem,
  WorkspaceIntegrationCatalogTool,
  WorkspaceIntegrationImportAuthType,
  WorkspaceIntegrationImportPreview,
  WorkspaceIntegrationImportRequest,
  WorkspaceIntegrationPolicy,
  WorkspaceIntegrationPolicyState,
  WorkspaceIntegrationProbeResponse,
  WorkspaceIntegrationMachineConsumer,
  WorkspaceIntegrationHealthResponse,
  WorkspaceIntegrationServiceStatus,
  WorkspaceIntegrationsResponse,
} from "../lib/api";
import type { AccountMember } from "../lib/types";

const CATALOG_FORMAT_LABELS: Record<CatalogFormat, string> = {
  prebuilt: "Pre-built",
  mcp: "MCP",
  api: "API import",
};

const GOOGLE_WORKSPACE_PERMISSION_IDS = ["gmail", "drive", "calendar", "docs", "sheets"] as const;
const EMPTY_WORKSPACE_INTEGRATIONS: WorkspaceIntegration[] = [];
const EMPTY_WORKSPACE_MACHINES: WorkspaceIntegrationMachineConsumer[] = [];

function catalogEntryOAuthProviderSlug(entry: CatalogEntry | null | undefined) {
  return entry?.oauthProviderSlug ?? catalogEntryConnectionSlug(entry);
}

function connectedCatalogEntries(catalogEntries: CatalogEntry[], integrations: WorkspaceIntegration[]) {
  return integrations.filter(isWorkspaceIntegrationConnected).flatMap((integration) => {
    const matches = catalogEntries.filter((candidate) => catalogEntryConnectionSlug(candidate) === integration.slug);
    if (integration.slug === GOOGLE_WORKSPACE_CONNECTION_SLUG || integration.kind === "google_workspace") {
      const serviceEntries = matches.filter((entry) => entry.googleService && googleServiceConfiguredForIntegration(entry, integration));
      if (serviceEntries.length > 0) {
        return serviceEntries.map((entry) => ({ integration, entry }));
      }
    }
    const entry = matches.find((candidate) => candidate.id !== integration.slug) ?? matches[0];
    return [{ integration, entry }];
  });
}

function catalogEntryConnectionDisabled(entry: CatalogEntry) {
  if (catalogEntryIsImport(entry)) return false;
  if (entry.id === CUSTOM_MCP_CATALOG_ID || entry.kind === "custom-mcp" || catalogEntryOAuthProviderSlug(entry) === GOOGLE_WORKSPACE_CONNECTION_SLUG) return false;
  // OAuth apps are connectable when the catalog carries the vendor's official
  // hosted MCP endpoint (probe -> OAuth with DCR/CIMD); without one there is no
  // per-provider OAuth implementation, so they stay "Coming soon".
  if (entry.authKind === "oauth") return !catalogEntryUsesMCPConnectFlow(entry);
  return !(entry.endpoint && entry.toolManifest);
}

function catalogEntryIsCustomMCP(entry: CatalogEntry | null | undefined) {
  return entry?.id === CUSTOM_MCP_CATALOG_ID || entry?.kind === "custom-mcp";
}

function statusClass(enabled: boolean) {
  return enabled
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
    : "border-border bg-[var(--color-surface)] text-text-tertiary";
}

function configStatusClass(configured: boolean) {
  return configured
    ? "border-amber-500/30 bg-amber-500/10 text-amber-500"
    : "border-border bg-[var(--color-surface)] text-text-tertiary";
}

function machineStatusClass(status: string) {
  switch (status) {
    case "running":
      return "text-emerald-500";
    case "error":
      return "text-red-500";
    case "provisioning":
    case "starting":
      return "text-amber-500";
    default:
      return "text-text-tertiary";
  }
}

function relativeTimeSince(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// Freshness chip for the verified-facts layer: outcome of the most recent
// test call, with its timestamp (integrations.sh's "discovered 9m ago").
function VerifiedChip({ lastTest }: { lastTest?: WorkspaceIntegrationLastTest }) {
  if (!lastTest) return null;
  const tone =
    lastTest.status === "ok"
      ? "border-green-500/30 bg-green-400/10 text-green-400"
      : lastTest.status === "skipped"
        ? "border-yellow-400/30 bg-yellow-400/10 text-yellow-400"
        : "border-red-500/30 bg-red-400/10 text-red-400";
  const label =
    lastTest.status === "ok"
      ? `verified ${relativeTimeSince(lastTest.at)}`
      : lastTest.status === "skipped"
        ? "not auto-tested"
        : `test failed ${relativeTimeSince(lastTest.at)}`;
  return (
    <span
      title={lastTest.note || undefined}
      className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] ${tone}`}
    >
      {label}
    </span>
  );
}

function ConnectorStatus({ connected }: { connected: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${statusClass(connected)}`}>
      {connected ? <CheckCircle2 className="h-3 w-3" /> : <Plug className="h-3 w-3" />}
      {connected ? "Added" : "Available"}
    </span>
  );
}

function IntegrationCatalogRow({
  entry,
  connected,
  canManage,
  disabled,
  href,
  onConnect,
  lastTest,
}: {
  entry: CatalogEntry;
  connected: boolean;
  canManage: boolean;
  disabled?: boolean;
  href: string;
  onConnect: () => void;
  lastTest?: WorkspaceIntegrationLastTest;
}) {
  const Icon = entry.Icon;
  const label = disabled ? "Coming soon" : connected ? (canManage ? "Manage" : "View") : canManage ? "Add" : "View";
  // Custom-MCP entries (generic or curated remote-MCP like Notion) must reach the
  // detail page to probe the server before connecting/OAuth — they can't use the
  // direct consent->connect flow (which has no probe step).
  const useConnectFlow = canManage && !disabled && !connected && entry.authKind === "oauth" && !catalogEntryUsesMCPConnectFlow(entry);
  const actionClass = connected
    ? "border border-border text-text-secondary hover:bg-[var(--color-surface-hover)]"
    : "border border-border bg-card text-text-primary hover:bg-[var(--color-surface-hover)]";
  return (
    <div className="grid gap-3 border-t border-border py-4 first:border-t-0 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
      <Link to={href} className="group flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] border border-border bg-card">
          <Icon className="h-5 w-5 text-text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold text-text-primary group-hover:text-brand-600">{entry.name}</h3>
            {connected && <ConnectorStatus connected />}
            <VerifiedChip lastTest={lastTest} />
          </div>
          <p className="mt-1 max-w-xl text-sm text-text-secondary">{entry.description}</p>
        </div>
      </Link>
      {useConnectFlow ? (
        <button
          type="button"
          onClick={onConnect}
          className={`inline-flex min-w-[92px] items-center justify-center rounded-[var(--radius-sm)] px-3 py-2 text-sm font-medium transition-colors ${actionClass}`}
        >
          {label}
        </button>
      ) : (
        <Link
          to={href}
          aria-disabled={disabled}
          className={`inline-flex min-w-[92px] items-center justify-center rounded-[var(--radius-sm)] px-3 py-2 text-sm font-medium transition-colors ${
            disabled
              ? "pointer-events-none border border-border text-text-tertiary opacity-60"
              : actionClass
          }`}
        >
          {label}
        </Link>
      )}
    </div>
  );
}

function ScopePill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-[var(--color-surface)] px-2 py-1 text-[11px] text-text-secondary">
      {children}
    </span>
  );
}

function ApprovalBadge({ label = "Approved by your admin" }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-500">
      <ShieldCheck className="h-3 w-3" />
      {label}
    </span>
  );
}

function googleServiceStatusItems(integration: WorkspaceIntegration | null): WorkspaceIntegrationServiceStatus[] {
  if (!integration || (integration.slug !== "google-workspace" && integration.kind !== "google_workspace")) {
    return [];
  }
  const statuses = integration.service_status ?? {};
  return ["gmail", "drive", "calendar", "docs", "sheets"].flatMap((service) => {
    const status = statuses[service];
    return status ? [{ ...status, service }] : [];
  });
}

function googleServiceLabel(service: string) {
  switch (service) {
    case "gmail":
      return "Gmail";
    case "drive":
      return "Google Drive";
    case "calendar":
      return "Google Calendar";
    case "docs":
      return "Google Docs";
    case "sheets":
      return "Google Sheets";
    default:
      return service;
  }
}

function googleServiceStatusLabel(status: string) {
  switch (status) {
    case "connected":
      return "Ready";
    case "missing_scope":
      return "Missing scope";
    case "admin_policy_blocked":
      return "Admin blocked";
    case "token_revoked":
      return "Reconnect";
    case "rate_limited":
      return "Rate limited";
    case "upstream_unavailable":
      return "Unavailable";
    case "off":
      return "Off";
    default:
      return status.replace(/_/g, " ");
  }
}

function googleServiceStatusClass(status: string) {
  switch (status) {
    case "connected":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-500";
    case "missing_scope":
    case "admin_policy_blocked":
    case "token_revoked":
      return "border-red-500/30 bg-red-500/10 text-red-500";
    case "rate_limited":
    case "upstream_unavailable":
      return "border-amber-500/30 bg-amber-500/10 text-amber-500";
    default:
      return "border-border bg-[var(--color-surface)] text-text-secondary";
  }
}

function googleServiceActionLabel(action?: string) {
  switch (action) {
    case "grant_scope_or_reconnect":
      return "Reconnect and grant access.";
    case "ask_google_workspace_admin":
      return "Ask a Google Workspace admin to allow access.";
    case "reconnect_integration":
      return "Reconnect the Google account.";
    case "retry_later":
      return "Retry later.";
    case "connect_service":
      return "Enable this service to expose tools.";
    default:
      return "";
  }
}

function GoogleServiceStatusPanel({ statuses }: { statuses: WorkspaceIntegrationServiceStatus[] }) {
  if (statuses.length === 0) return null;
  return (
    <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3">
      <h3 className="text-sm font-semibold text-text-primary">Google services</h3>
      <div className="mt-3">
        {statuses.map((status) => {
          const action = googleServiceActionLabel(status.action);
          return (
            <div key={status.service} className="border-t border-border py-2 first:border-t-0 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-text-primary">{googleServiceLabel(status.service)}</div>
                <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] ${googleServiceStatusClass(status.status)}`}>
                  {googleServiceStatusLabel(status.status)}
                </span>
              </div>
              {(status.detail || action) && (
                <div className="mt-1 text-xs text-text-secondary">
                  {status.detail || action}
                  {status.detail && action ? ` ${action}` : ""}
                </div>
              )}
              {status.http_status && (
                <div className="mt-1 text-[11px] text-text-tertiary">HTTP {status.http_status}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MachineRuntimeRow({
  machine,
  hasConfiguredIntegration,
}: {
  machine: WorkspaceIntegrationMachineConsumer;
  hasConfiguredIntegration: boolean;
}) {
  return (
    <tr className="border-t border-border">
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-text-tertiary" />
          <span className="text-sm font-medium text-text-primary">{machine.name}</span>
        </div>
      </td>
      <td className={`py-3 pr-4 text-sm capitalize ${machineStatusClass(machine.status)}`}>{machine.status}</td>
      <td className="py-3 pr-4">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${configStatusClass(machine.plugin_enabled)}`}>
          {machine.plugin_enabled ? <Wrench className="h-3 w-3" /> : <Plug className="h-3 w-3" />}
          {machine.plugin_enabled ? "Config enabled" : "Not configured"}
        </span>
      </td>
      <td className="py-3 text-right">
        {machine.plugin_enabled ? (
          <div className="text-xs text-text-tertiary">
            <div>Active on next agent run</div>
            {machine.installed_version && <div className="mt-0.5">Catalog {machine.installed_version}</div>}
          </div>
        ) : hasConfiguredIntegration ? (
          <span className="text-xs text-text-tertiary">Assigns on next config push</span>
        ) : (
          <span className="text-xs text-text-tertiary">Waiting for an app</span>
        )}
      </td>
    </tr>
  );
}

function integrationPath(workspaceId: string | undefined, catalogId?: string) {
  const base = workspaceId ? `/workspaces/${workspaceId}/integrations` : "/integrations";
  return catalogId ? `${base}/${catalogId}` : base;
}

function trustedOAuthRedirect(raw: string) {
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin === window.location.origin || parsed.protocol === "https:") return true;
    return parsed.protocol === "http:"
      && localOAuthHost(window.location.hostname)
      && localOAuthHost(parsed.hostname);
  } catch {
    return false;
  }
}

function localOAuthHost(hostname: string) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname === "[::1]";
}

let redirectToWorkspaceIntegrationOAuth = (url: string) => window.location.assign(url);

export function setWorkspaceIntegrationOAuthRedirectForTest(redirect?: (url: string) => void) {
  redirectToWorkspaceIntegrationOAuth = redirect ?? ((url: string) => window.location.assign(url));
}

function memberDisplayName(members: AccountMember[], userId?: number) {
  if (!userId) return "Unknown user";
  const member = members.find((candidate) => candidate.user_id === userId);
  return member?.name || member?.email || `User ${userId}`;
}

function formatConnectionTime(value?: string) {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function catalogDisplayName(entry: CatalogEntry, values: Record<string, string>) {
  if (entry.id === "github") {
    const owner = catalogFieldValue(values, "owner");
    const repo = catalogFieldValue(values, "repo");
    if (owner && repo) return `${entry.name} ${owner}/${repo}`;
  }
  return entry.name;
}

function workspaceIntegrationImportRequestFromFields(entry: CatalogEntry, values: Record<string, string>): WorkspaceIntegrationImportRequest {
  const type = entry.kind === "graphql-import" ? "graphql" : "openapi";
  const authType = (catalogFieldValue(values, "auth_type") || "none") as WorkspaceIntegrationImportAuthType;
  const authHeader = catalogFieldValue(values, "auth_header");
  const request: WorkspaceIntegrationImportRequest = {
    type,
    slug: catalogFieldValue(values, "slug"),
    display_name: catalogFieldValue(values, "display_name"),
    spec_text: catalogFieldValue(values, "spec_text"),
    spec_url: type === "openapi" ? catalogFieldValue(values, "spec_url") : undefined,
    base_url: type === "openapi" ? catalogFieldValue(values, "base_url") : undefined,
    endpoint: type === "graphql" ? catalogFieldValue(values, "endpoint") : undefined,
    auth: {
      type: authType,
      ...(authHeader ? { header: authHeader } : {}),
    },
  };
  const token = catalogFieldValue(values, "token");
  if (token) {
    request.token = token;
  }
  return request;
}

function importPreviewTools(preview: WorkspaceIntegrationImportPreview | null): CatalogEntry["tools"] {
  const tools = Array.isArray(preview?.tools) ? preview.tools : [];
  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description || "Imported tool.",
    access: tool.access,
    mode: tool.mode,
    source: tool.source,
  }));
}

function workspaceIntegrationImportAuthLabel(authKind: WorkspaceIntegrationImportAuthType) {
  switch (authKind) {
    case "none":
      return "No auth";
    case "bearer":
      return "Bearer token";
    case "api_key_header":
      return "API key header";
    case "oauth":
      return "OAuth access token";
  }
}

function permissionSelectionFromEntry(entry: CatalogEntry, integration?: WorkspaceIntegration | null): OAuthPermissionSelection {
  const selection: OAuthPermissionSelection = {};
  for (const permission of entry.oauthPermissions ?? []) {
    const raw = integration?.permission_levels?.[permission.id];
    const allowed = permission.options.some((option) => option.value === raw);
    selection[permission.id] = allowed ? (raw as PermissionLevel) : permission.defaultLevel;
  }
  return selection;
}

function oauthPayloadForEntry(entry: CatalogEntry, selection: OAuthPermissionSelection) {
  if (!entry.oauthPermissions?.length) return undefined;
  if (catalogEntryOAuthProviderSlug(entry) === GOOGLE_WORKSPACE_CONNECTION_SLUG) {
    const permissions: OAuthPermissionSelection = {};
    for (const id of GOOGLE_WORKSPACE_PERMISSION_IDS) {
      permissions[id] = "off";
    }
    return googleWorkspaceOAuthPayload({ ...permissions, ...selection });
  }
  return googleWorkspaceOAuthPayload(selection);
}

function isWorkspaceIntegrationConnected(integration?: WorkspaceIntegration | null) {
  return Boolean(integration?.connected_at || integration?.target || integration?.enabled);
}

const emptyPolicyDraft: PolicyDraft = {
  allowedTools: [],
  deniedTools: [],
  policies: [],
  supportsAsk: false,
};

function policyDraftFromTools(
  tools: Array<Pick<WorkspaceIntegrationCatalogTool, "name">>,
  allowed: string[] | undefined,
  denied: string[] | undefined,
  policies: WorkspaceIntegrationPolicy[] | undefined,
): PolicyDraft {
  const toolNames = tools.map((tool) => tool.name).filter(Boolean);
  const explicit = new Map<string, WorkspaceIntegrationPolicyState>();
  for (const policy of policies ?? []) {
    if (!policy.tool_name.trim()) continue;
    explicit.set(policy.tool_name, normalizeWorkspaceIntegrationPolicyState(policy.policy));
  }
  const supportsAsk = explicit.size > 0;
  if (!supportsAsk) {
    return {
      allowedTools: allowed ?? [],
      deniedTools: denied ?? [],
      policies: [],
      supportsAsk: false,
    };
  }
  if (toolNames.length === 0 && explicit.size > 0) {
    return policyDraftFromPolicies(Array.from(explicit, ([tool_name, policy]) => ({ tool_name, policy })), true);
  }
  const allowedList = allowed ?? [];
  const deniedList = denied ?? [];
  const entries = toolNames.map((toolName) => ({
    tool_name: toolName,
    policy: explicit.get(toolName) ?? legacyPolicyState(allowedList, deniedList, toolName),
  }));
  return policyDraftFromPolicies(entries, supportsAsk);
}

function policyStateForTool(draft: PolicyDraft, toolName: string): WorkspaceIntegrationPolicyState {
  if (draft.supportsAsk) {
    const entry = draft.policies.find((policy) => policy.tool_name === toolName);
    if (entry) return entry.policy;
  }
  return legacyPolicyState(draft.allowedTools, draft.deniedTools, toolName);
}

function updatePolicyTool(values: string[], toolName: string, enabled: boolean) {
  if (enabled) {
    return values.includes(toolName) ? values : [...values, toolName];
  }
  return values.filter((candidate) => candidate !== toolName);
}

function updatePolicyDraftState(previous: PolicyDraft, toolName: string, state: WorkspaceIntegrationPolicyState): PolicyDraft {
  const allowedTools = updatePolicyTool(previous.allowedTools, toolName, state === "allow");
  const deniedTools = updatePolicyTool(previous.deniedTools, toolName, state === "block");
  if (!previous.supportsAsk) {
    return { ...previous, allowedTools, deniedTools };
  }
  const policies = [...previous.policies];
  const index = policies.findIndex((policy) => policy.tool_name === toolName);
  const next = { tool_name: toolName, policy: state };
  if (index >= 0) {
    policies[index] = next;
  } else {
    policies.push(next);
  }
  return { ...previous, allowedTools, deniedTools, policies };
}

function ModalOverlay({
  labelledBy,
  maxWidthClass,
  onClose,
  children,
}: {
  labelledBy: string;
  maxWidthClass: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialogRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={`w-full ${maxWidthClass} rounded-[var(--radius-sm)] border border-border bg-card p-5 shadow-xl outline-none`}
      >
        {children}
      </div>
    </div>
  );
}

function ConnectConsentModal({
  entry,
  busy,
  advancedOpen,
  permissionSelection,
  onAdvancedToggle,
  onPermissionChange,
  onCancel,
  onContinue,
}: {
  entry: CatalogEntry;
  busy: boolean;
  advancedOpen: boolean;
  permissionSelection: OAuthPermissionSelection;
  onAdvancedToggle: () => void;
  onPermissionChange: (serviceId: string, level: PermissionLevel) => void;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const isOAuth = entry.authKind === "oauth";
  return (
    <ModalOverlay labelledBy="connect-consent-title" maxWidthClass="max-w-lg" onClose={busy ? () => {} : onCancel}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-[var(--radius-sm)] bg-text-primary text-white">
              <Plug className="h-7 w-7" />
            </div>
            <div className="flex gap-1">
              <span className="h-2 w-2 rounded-full bg-border" />
              <span className="h-2 w-2 rounded-full bg-border" />
              <span className="h-2 w-2 rounded-full bg-border" />
            </div>
            <div className="flex h-14 w-14 items-center justify-center rounded-[var(--radius-sm)] border border-border bg-card">
              <entry.Icon className="h-7 w-7 text-text-primary" />
            </div>
          </div>
          <h2 id="connect-consent-title" className="mt-5 text-2xl font-semibold text-text-primary">
            {isOAuth ? "Connect" : "Add"} {entry.name}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">Developed by {entry.developer}</p>
          <div className="mt-4 flex justify-center">
            <ApprovalBadge label="Approved for this workspace" />
          </div>
        </div>

        <div className="mt-5 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-4">
          <div className="border-b border-border pb-3">
            <h3 className="text-sm font-semibold text-text-primary">Permissions always respected</h3>
            <p className="mt-1 text-sm text-text-secondary">
              OpenClaw is limited to the permissions you grant. You can disconnect this integration anytime.
            </p>
          </div>
          <div className="border-b border-border py-3">
            <h3 className="text-sm font-semibold text-text-primary">You're in control</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Machines in this workspace can use {entry.name} only through the enabled workspace tools.
            </p>
          </div>
          <div className="pt-3">
            <h3 className="text-sm font-semibold text-text-primary">Integrations may introduce risk</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Connected services can expose sensitive workspace data. Review access before continuing.
            </p>
          </div>
        </div>

        {entry.oauthPermissions && (
          <div className="mt-5 rounded-[var(--radius-sm)] border border-border bg-card p-4">
            <h3 className="text-sm font-semibold text-text-primary">Choose access</h3>
            <div className="mt-3 space-y-3">
              {entry.oauthPermissions.map((permission) => (
                <div key={permission.id} className="grid gap-2 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                  <div>
                    <div className="text-sm font-medium text-text-primary">{permission.label}</div>
                    <div className="mt-0.5 text-xs text-text-tertiary">{permission.description}</div>
                  </div>
                  <div className="inline-flex rounded-[var(--radius-sm)] border border-border bg-card p-1">
                    {permission.options.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onPermissionChange(permission.id, option.value)}
                        disabled={busy}
                        title={option.description}
                        className={`rounded-[var(--radius-sm)] px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          permissionSelection[permission.id] === option.value
                            ? "bg-text-primary text-white"
                            : "text-text-secondary hover:bg-[var(--color-surface-hover)]"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {advancedOpen && (
          <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3 text-sm text-text-secondary">
            <InfoRow label="Transport" value={entry.transport} />
            <div className="mt-2">
              <InfoRow label="Auth" value={entry.authKind} />
            </div>
            {catalogEntryIsCustomMCP(entry) && (
              <div className="mt-2">
                <InfoRow label="Resources/prompts" value="Not connected in v1" />
              </div>
            )}
            <div className="mt-3 space-y-2 border-t border-border pt-3">
              {entry.tools.map((tool) => (
                <div key={tool.name} className="flex items-start justify-between gap-3 text-xs">
                  <span className="font-medium text-text-primary">{tool.name}</span>
                  <ScopePill>{tool.access}</ScopePill>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-[var(--color-surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAdvancedToggle}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-[var(--color-surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Advanced settings
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-text-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {isOAuth ? `Continue to ${entry.developer}` : `Add ${entry.name}`}
          </button>
        </div>
    </ModalOverlay>
  );
}

export function WorkspaceIntegrations() {
  const { workspaceId, catalogId } = useParams<{ workspaceId?: string; catalogId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { account, user } = useAuth();
  const [data, setData] = useState<WorkspaceIntegrationsResponse | null>(null);
  const [health, setHealth] = useState<WorkspaceIntegrationHealthResponse | null>(null);
  const [approvalRequests, setApprovalRequests] = useState<WorkspaceIntegrationApprovalRequest[]>([]);
  const [actingApprovalId, setActingApprovalId] = useState<string | null>(null);
  const [catalogItems, setCatalogItems] = useState<WorkspaceIntegrationCatalogItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [members, setMembers] = useState<AccountMember[]>([]);
  const [memberRole, setMemberRole] = useState<AccountMember["role"] | null>(null);
  const [savingCatalogId, setSavingCatalogId] = useState<string | null>(null);
  const [testingCatalogId, setTestingCatalogId] = useState<string | null>(null);
  const [revokingCatalogId, setRevokingCatalogId] = useState<string | null>(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [policyDraft, setPolicyDraft] = useState<PolicyDraft>(emptyPolicyDraft);
  const [integrationTestResult, setIntegrationTestResult] = useState<Record<string, unknown> | null>(null);
  const [consentEntry, setConsentEntry] = useState<CatalogEntry | null>(null);
  // The catalog browser is collapsed by default (the copilot chat is the
  // primary add surface); the chat's "Browse all" / "Use classic form" links
  // target #integration-catalog and expand it.
  const [catalogOpen, setCatalogOpen] = useState(() => window.location.hash === "#integration-catalog");
  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === "#integration-catalog") setCatalogOpen(true);
    };
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  // "/" focuses catalog search from anywhere on the page (integrations.sh
  // pattern), expanding the collapsed browser first.
  const catalogSearchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const onSlash = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      event.preventDefault();
      setCatalogOpen(true);
      // Focus after the details section has rendered its content.
      window.setTimeout(() => catalogSearchRef.current?.focus(), 0);
    };
    window.addEventListener("keydown", onSlash);
    return () => window.removeEventListener("keydown", onSlash);
  }, []);
  const [advancedConsentOpen, setAdvancedConsentOpen] = useState(false);
  const [permissionSelection, setPermissionSelection] = useState<OAuthPermissionSelection>({});
  const [customProbe, setCustomProbe] = useState<WorkspaceIntegrationProbeResponse | null>(null);
  const [customOAuthScopes, setCustomOAuthScopes] = useState<string[]>([]);
  const [customOAuthClientId, setCustomOAuthClientId] = useState("");
  const [probingCustomMCP, setProbingCustomMCP] = useState(false);
  const [importPreview, setImportPreview] = useState<WorkspaceIntegrationImportPreview | null>(null);
  const [previewingImport, setPreviewingImport] = useState(false);
  const mountedRef = useRef(false);
  const loadRequestSeq = useRef(0);
  const integrationTestRequestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadRequestSeq.current += 1;
      integrationTestRequestSeq.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    if (!account) return;
    const requestSeq = loadRequestSeq.current + 1;
    loadRequestSeq.current = requestSeq;
    const isCurrent = () => mountedRef.current && loadRequestSeq.current === requestSeq;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const healthSince = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [next, catalog, nextHealth, nextApprovals] = await Promise.all([
        listWorkspaceIntegrations(account.id, workspaceId),
        listWorkspaceIntegrationCatalog(account.id, workspaceId).catch(() => null),
        workspaceId ? listWorkspaceIntegrationHealth(account.id, workspaceId, healthSince).catch(() => null) : Promise.resolve(null),
        listWorkspaceIntegrationApprovalRequests(account.id, workspaceId).catch(() => null),
      ]);
      if (isCurrent()) {
        setData(next);
        setCatalogItems(catalog?.integrations ?? null);
        setHealth(nextHealth);
        setApprovalRequests(nextApprovals?.approval_requests ?? []);
      }
    } catch (err: unknown) {
      if (!isCurrent()) return;
      const message = err instanceof Error ? err.message : "Failed to load workspace integrations.";
      setError(message);
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [account, workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  // Buttons are gated on canManage; the backend enforces the role as well.
  const handleResolveApproval = useCallback(async (requestId: string, decision: "approve" | "deny") => {
    if (!account) return;
    setActingApprovalId(requestId);
    setError(null);
    try {
      if (decision === "approve") {
        await approveWorkspaceIntegrationApprovalRequest(account.id, requestId, workspaceId);
      } else {
        await denyWorkspaceIntegrationApprovalRequest(account.id, requestId, workspaceId);
      }
      const refreshed = await listWorkspaceIntegrationApprovalRequests(account.id, workspaceId).catch(() => null);
      if (mountedRef.current) {
        setApprovalRequests(refreshed?.approval_requests ?? []);
        setNotice(decision === "approve"
          ? "Approved. The agent can retry the tool call with its token now."
          : "Denied. The agent will be told the call was rejected.");
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to resolve the approval request.");
    } finally {
      if (mountedRef.current) {
        setActingApprovalId(null);
      }
    }
  }, [account, workspaceId]);

  useEffect(() => {
    if (!account || !user) return;
    let cancelled = false;
    setMembers([]);
    setMemberRole(null);
    listMembers(account.id)
      .then((members) => {
        if (cancelled) return;
        setMembers(members);
        const current = members.find((member) => member.user_id === user.id);
        setMemberRole(current?.role ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setMembers([]);
          setMemberRole(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [account, user]);

  useEffect(() => {
    if ((searchParams.get("google-workspace") ?? searchParams.get("google")) !== "error") return;
    setError(searchParams.get("message") || "Google Workspace connection failed.");
  }, [searchParams]);

  const integrations = Array.isArray(data?.integrations) ? data.integrations : EMPTY_WORKSPACE_INTEGRATIONS;
  const machines = Array.isArray(data?.machines) ? data.machines : EMPTY_WORKSPACE_MACHINES;
  const workspaceName = data?.workspace.name ?? "Workspace";
  const normalizedCatalogItems = Array.isArray(catalogItems) ? catalogItems : null;
  const baseCatalogEntries = useMemo(() => catalogEntriesFromBackend(normalizedCatalogItems, [], CATALOG), [normalizedCatalogItems]);
  const catalogEntries = useMemo(() => catalogEntriesFromBackend(normalizedCatalogItems, integrations, CATALOG), [normalizedCatalogItems, integrations]);
  const selectedEntry = useMemo(() => catalogEntries.find((entry) => entry.id === catalogId) ?? null, [catalogEntries, catalogId]);
  const integrationBySlug = useMemo(() => new Map(integrations.map((integration) => [integration.slug, integration])), [integrations]);
  const selectedIntegrationSlug = catalogEntryConnectionSlug(selectedEntry);
  const selectedIntegration = selectedEntry ? integrationBySlug.get(selectedIntegrationSlug) ?? null : null;
  const selectedIntegrationConnected = isWorkspaceIntegrationConnected(selectedIntegration);
  const configuredCount = integrations.filter(isWorkspaceIntegrationConnected).length;
  const hasConfiguredIntegration = configuredCount > 0;
  const canManage = memberRole === "owner" || memberRole === "admin";
  const configuredMachineCount = machines.filter((machine) => machine.plugin_enabled).length;
  const healthTools = Array.isArray(health?.tools) ? health.tools : [];
  const unhealthyToolCount = healthTools.filter((tool) => tool.error_calls > 0 || tool.success_rate < 0.99).length;

  const addedEntries = useMemo(() => connectedCatalogEntries(catalogEntries, integrations), [catalogEntries, integrations]);

  const [catalogFormat, setCatalogFormat] = useState<CatalogFormat | "all">("all");

  const searchableCatalog = useMemo(
    () => baseCatalogEntries.filter((entry) => entry.id !== GOOGLE_WORKSPACE_CONNECTION_SLUG),
    [baseCatalogEntries],
  );

  const searchMatchedCatalog = useMemo(
    () => searchableCatalog.filter((entry) => catalogSearchMatches(entry, searchQuery)),
    [searchableCatalog, searchQuery],
  );

  const catalogFormatCounts = useMemo(() => {
    const counts: Record<CatalogFormat, number> = { prebuilt: 0, mcp: 0, api: 0 };
    for (const entry of searchMatchedCatalog) {
      counts[catalogEntryFormat(entry)] += 1;
    }
    return counts;
  }, [searchMatchedCatalog]);

  const filteredCatalog = useMemo(
    () =>
      catalogFormat === "all"
        ? searchMatchedCatalog
        : searchMatchedCatalog.filter((entry) => catalogEntryFormat(entry) === catalogFormat),
    [catalogFormat, searchMatchedCatalog],
  );

  const catalogByCategory = useMemo(() => {
    const groups = new Map<string, CatalogEntry[]>();
    for (const entry of filteredCatalog) {
      const group = groups.get(entry.category) ?? [];
      group.push(entry);
      groups.set(entry.category, group);
    }
    return [...groups.entries()];
  }, [filteredCatalog]);

  useEffect(() => {
    if (!selectedEntry) return;
    setCustomProbe(null);
    setImportPreview(null);
    if (!selectedEntry.fields?.length) {
      setFieldValues({});
      return;
    }
    const current = integrationBySlug.get(selectedEntry.id);
    const [owner = "", repo = ""] = current?.target?.split("/", 2) ?? [];
    setFieldValues((previous) => {
      const next: Record<string, string> = {};
      for (const field of selectedEntry.fields ?? []) {
        if (field.secret) {
          next[field.id] = "";
          continue;
        }
        const fallback = field.id === "owner"
          ? owner
          : field.id === "repo"
            ? repo
            : field.id === "url"
              ? current?.target ?? ""
              : field.id === "auth_type"
                ? "none"
                : "";
        next[field.id] = previous[field.id] || fallback;
      }
      return next;
    });
  }, [integrationBySlug, selectedEntry]);

  useEffect(() => {
    if (catalogEntryIsImport(selectedEntry) && importPreview) {
      setPolicyDraft(policyDraftFromTools(importPreview.tools ?? [], importPreview.allowed_tools, importPreview.denied_tools, undefined));
      return;
    }
    if (!selectedIntegration) {
      setPolicyDraft(emptyPolicyDraft);
      return;
    }
    setPolicyDraft(policyDraftFromTools(
      selectedIntegration.tools ?? [],
      selectedIntegration.allowed_tools,
      selectedIntegration.denied_tools,
      selectedIntegration.policies,
    ));
  }, [importPreview, selectedEntry, selectedIntegration]);

  const handlePreviewImport = useCallback(async () => {
    if (!account || !canManage || !selectedEntry || !catalogEntryIsImport(selectedEntry)) return;
    setPreviewingImport(true);
    setError(null);
    setNotice(null);
    setImportPreview(null);
    try {
      const request = workspaceIntegrationImportRequestFromFields(selectedEntry, fieldValues);
      delete request.token;
      const response = await previewWorkspaceIntegrationImport(account.id, request, workspaceId);
      if (!mountedRef.current) return;
      setImportPreview(response);
      setPolicyDraft(policyDraftFromTools(response.tools ?? [], response.allowed_tools, response.denied_tools, undefined));
      setNotice(`Generated ${response.tools.length} ${response.tools.length === 1 ? "tool" : "tools"} from ${response.display_name}.`);
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : "Failed to preview imported integration.";
      setError(message);
    } finally {
      if (mountedRef.current) {
        setPreviewingImport(false);
      }
    }
  }, [account, canManage, fieldValues, selectedEntry, workspaceId]);

  const handleSaveImport = useCallback(async (entry: CatalogEntry) => {
    if (!account || !canManage || !catalogEntryIsImport(entry)) return;
    if (!importPreview) {
      throw new Error("Preview the imported integration before saving it.");
    }
    const request = workspaceIntegrationImportRequestFromFields(entry, fieldValues);
    const saved = await createWorkspaceIntegrationImport(account.id, request, workspaceId);
    await updateWorkspaceIntegrationPolicy(account.id, saved.slug, {
      allowed_tools: policyDraft.allowedTools,
      denied_tools: policyDraft.deniedTools,
    }, workspaceId);
    await load();
    if (mountedRef.current) {
      setNotice(`${saved.display_name} connected.`);
      setImportPreview(null);
      navigate(integrationPath(workspaceId, saved.slug), { replace: true });
    }
  }, [account, canManage, fieldValues, importPreview, load, navigate, policyDraft.allowedTools, policyDraft.deniedTools, workspaceId]);

  const executeConnect = useCallback(async (entry: CatalogEntry) => {
    if (!account || !canManage) return;
    setSavingCatalogId(entry.id);
    setError(null);
    setIntegrationTestResult(null);
    try {
      if (catalogEntryIsImport(entry)) {
        await handleSaveImport(entry);
      } else if (catalogEntryUsesMCPConnectFlow(entry)) {
        const endpoint = catalogFieldValue(fieldValues, "url") || entry.endpoint || "";
        if (!customProbe || customProbe.url !== endpoint) {
          throw new Error("Probe the remote MCP server before adding it.");
        }
        const probedTools = customMCPToolsFromProbe(customProbe.tools);
        const policy = customMCPInitialPolicy(probedTools);
        const slug = entry.id === CUSTOM_MCP_CATALOG_ID
          ? deriveCustomMCPSlug({
            url: endpoint,
            serverName: customProbe.server.name,
            existingSlugs: [...catalogEntries.map((entry) => entry.id), ...integrations.map((integration) => integration.slug)],
          })
          : entry.id;
        const token = catalogSecret(entry, fieldValues);
        const displayName = customProbe.server.name || (entry.id === CUSTOM_MCP_CATALOG_ID ? endpoint : entry.name);
        await createWorkspaceIntegration(account.id, slug, {
          display_name: displayName,
          kind: "custom-mcp",
          transport: "mcp-remote",
          endpoint,
          tool_manifest: customMCPManifestFromProbe(customProbe.tools),
          config: {
            display_target: endpoint,
            server_name: customProbe.server.name,
            server_version: customProbe.server.version,
            protocol_version: customProbe.protocol_version,
            probed_at: customProbe.probed_at,
          },
          ...(token ? { token, token_type: entry.tokenType ?? "bearer" } : {}),
        }, workspaceId);
        await updateWorkspaceIntegrationPolicy(account.id, slug, {
          allowed_tools: policy.allowedTools,
          denied_tools: policy.deniedTools,
          ...(policy.supportsAsk ? { policies: policy.policies } : {}),
        }, workspaceId);
        await load();
        if (mountedRef.current) {
          setNotice(`${displayName} ${entry.id === CUSTOM_MCP_CATALOG_ID ? "connected" : "updated"}.`);
          setCustomProbe(null);
          navigate(integrationPath(workspaceId, slug), { replace: true });
        }
      } else if (entry.authKind === "bearer") {
        if (!entry.endpoint || !entry.toolManifest) {
          throw new Error(`${entry.name} is missing catalog connection data.`);
        }
        const token = catalogSecret(entry, fieldValues);
        await createWorkspaceIntegration(account.id, entry.id, {
          display_name: catalogDisplayName(entry, fieldValues),
          kind: entry.kind ?? entry.id,
          transport: entry.transport,
          endpoint: entry.endpoint,
          tool_manifest: entry.toolManifest,
          config: catalogConfig(entry, fieldValues),
          ...(token ? { token, token_type: entry.tokenType ?? "bearer" } : {}),
        }, workspaceId);
        await load();
        if (mountedRef.current) {
          setNotice(`${entry.name} connected.`);
          navigate(integrationPath(workspaceId, entry.id), { replace: true });
        }
      } else if (catalogEntryOAuthProviderSlug(entry) === GOOGLE_WORKSPACE_CONNECTION_SLUG) {
        const response = await startWorkspaceIntegrationOAuth(account.id, GOOGLE_WORKSPACE_CONNECTION_SLUG, workspaceId, oauthPayloadForEntry(entry, permissionSelection));
        if (!trustedOAuthRedirect(response.url)) {
          throw new Error("Google OAuth returned an untrusted redirect URL.");
        }
        redirectToWorkspaceIntegrationOAuth(response.url);
      } else {
        throw new Error(`${entry.name} is not available in this workspace yet.`);
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : `Failed to connect ${entry.name}.`;
      setError(message);
    } finally {
      if (mountedRef.current) {
        setSavingCatalogId(null);
      }
    }
  }, [account, canManage, catalogEntries, customOAuthClientId, customProbe, fieldValues, handleSaveImport, integrations, load, navigate, permissionSelection, workspaceId]);

  const handleFieldValueChange = useCallback((fieldId: string, value: string) => {
    setFieldValues((previous) => ({ ...previous, [fieldId]: value }));
    if (catalogEntryIsCustomMCP(selectedEntry) && fieldId === "url") {
      setCustomProbe(null);
    }
    if (catalogEntryIsImport(selectedEntry)) {
      setImportPreview(null);
    }
  }, [selectedEntry]);

  const handleProbeCustomMCP = useCallback(async () => {
    if (!account || !canManage) return;
    // Generic custom-MCP reads the user-entered URL; curated entries use their
    // fixed catalog endpoint.
    const endpoint = catalogFieldValue(fieldValues, "url") || selectedEntry?.endpoint || "";
    if (!endpoint) {
      setError("Remote MCP URL is required.");
      return;
    }
    setProbingCustomMCP(true);
    setError(null);
    setCustomProbe(null);
    try {
      const token = selectedEntry ? catalogSecret(selectedEntry, fieldValues) : "";
      const response = await probeWorkspaceIntegration(account.id, {
        url: endpoint,
        ...(token ? { token } : {}),
      }, workspaceId);
      if (!mountedRef.current) return;
      setCustomProbe(response);
      setCustomOAuthScopes([]);
      setCustomOAuthClientId("");
      const tools = customMCPToolsFromProbe(response.tools);
      const policy = customMCPInitialPolicy(tools);
      setPolicyDraft(policy);
      if (response.auth_required && response.oauth?.available) {
        setNotice(`OAuth metadata detected for ${response.server.name || endpoint}. Bearer/API-key remains available while generic OAuth connection is finalized.`);
      } else {
        setNotice(`Discovered ${response.tools.length} ${response.tools.length === 1 ? "tool" : "tools"} from ${response.server.name || endpoint}.`);
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : "Failed to probe remote MCP server.";
      setError(message);
    } finally {
      if (mountedRef.current) {
        setProbingCustomMCP(false);
      }
    }
  }, [account, canManage, fieldValues, selectedEntry, workspaceId]);

  const handleCustomMCPOAuthContinue = useCallback(async () => {
    if (!account || !canManage || !selectedEntry || !customProbe?.oauth?.available) return;
    const endpoint = catalogFieldValue(fieldValues, "url") || selectedEntry.endpoint || "";
    if (!endpoint) {
      setError("Remote MCP URL is required.");
      return;
    }
    // Curated entries keep their catalog slug; only the generic custom-MCP entry
    // derives a slug from the URL/server name.
    const slug = selectedEntry.id === CUSTOM_MCP_CATALOG_ID
      ? deriveCustomMCPSlug({
        url: endpoint,
        serverName: customProbe.server.name,
        existingSlugs: [...catalogEntries.map((entry) => entry.id), ...integrations.map((integration) => integration.slug)],
      })
      : selectedEntry.id;
    setSavingCatalogId(selectedEntry.id);
    setError(null);
    try {
      const response = await startWorkspaceIntegrationOAuth(account.id, slug, workspaceId, {
        url: endpoint,
        display_name: customProbe.server.name || endpoint,
        scopes: customOAuthScopes,
        ...(customProbe.oauth.dynamic_client_registration ? {} : { client_id: customOAuthClientId.trim() }),
      });
      if (!trustedOAuthRedirect(response.url)) {
        throw new Error("Remote MCP OAuth returned an untrusted redirect URL.");
      }
      redirectToWorkspaceIntegrationOAuth(response.url);
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : "Failed to start remote MCP OAuth.";
      setError(message);
    } finally {
      if (mountedRef.current) {
        setSavingCatalogId(null);
      }
    }
  }, [account, canManage, catalogEntries, customOAuthClientId, customOAuthScopes, customProbe, fieldValues, integrations, selectedEntry, workspaceId]);

  const handleConnect = useCallback((entry: CatalogEntry) => {
    if (!account || !canManage) return;
    setError(null);
    setIntegrationTestResult(null);
    setAdvancedConsentOpen(false);
    setPermissionSelection(permissionSelectionFromEntry(entry, integrationBySlug.get(catalogEntryConnectionSlug(entry))));
    setConsentEntry(entry);
  }, [account, canManage, integrationBySlug]);

  // One-tap chips in the copilot chat. OAuth apps connect in a single click
  // with the catalog's default (read-biased) permissions; curated MCP apps
  // flow through the copilot's analyze pipeline with their known endpoint.
  const copilotQuickAdds = useMemo<CopilotQuickAdd[]>(() => {
    const out: CopilotQuickAdd[] = [];
    for (const entry of catalogEntries) {
      if (out.length >= 6) break;
      const integration = integrationBySlug.get(catalogEntryConnectionSlug(entry));
      const connected = isWorkspaceIntegrationConnected(integration);
      if (!connected && catalogEntryConnectionDisabled(entry)) continue;
      if (catalogEntryIsImport(entry) || entry.id === CUSTOM_MCP_CATALOG_ID) continue;
      let kind: CopilotQuickAdd["kind"] = "detail";
      if (catalogEntryOAuthProviderSlug(entry) === GOOGLE_WORKSPACE_CONNECTION_SLUG) {
        kind = "oauth";
      } else if (entry.transport === "mcp-remote" && entry.endpoint && catalogEntryUsesMCPConnectFlow(entry)) {
        kind = "mcp";
      }
      out.push({
        id: entry.id,
        name: entry.name,
        Icon: entry.Icon,
        connected,
        kind,
        endpoint: entry.endpoint,
        developer: entry.developer,
        permissionsSummary: kind === "oauth" ? COPILOT_DEFAULT_PERMISSIONS_SUMMARY : undefined,
      });
    }
    return out;
  }, [catalogEntries, integrationBySlug]);

  const handleCopilotOAuthConnect = useCallback(async (entryId: string) => {
    if (!account || !canManage) throw new Error("You need owner or admin access to connect apps.");
    const entry = catalogEntries.find((candidate) => candidate.id === entryId);
    if (!entry) throw new Error("This app is not available in the catalog.");
    const selection = permissionSelectionFromEntry(entry, integrationBySlug.get(catalogEntryConnectionSlug(entry)));
    const response = await startWorkspaceIntegrationOAuth(
      account.id,
      GOOGLE_WORKSPACE_CONNECTION_SLUG,
      workspaceId,
      oauthPayloadForEntry(entry, selection),
    );
    if (!trustedOAuthRedirect(response.url)) {
      throw new Error(`${entry.name} OAuth returned an untrusted redirect URL.`);
    }
    redirectToWorkspaceIntegrationOAuth(response.url);
  }, [account, canManage, catalogEntries, integrationBySlug, workspaceId]);

  const handleCopilotOpenDetail = useCallback((entryId: string) => {
    navigate(integrationPath(workspaceId, entryId));
  }, [navigate, workspaceId]);

  // Search never dead-ends (integrations.sh pattern): a zero-result query that
  // looks like a domain or URL becomes a "detect it with the copilot" row.
  const [copilotDetect, setCopilotDetect] = useState<{ input: string; nonce: number } | null>(null);
  const detectableQuery = useMemo(() => {
    const query = searchQuery.trim();
    if (!query || query.includes(" ")) return null;
    if (/^https?:\/\/\S+$/i.test(query)) return query;
    if (/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(\/\S*)?$/i.test(query)) return query;
    return null;
  }, [searchQuery]);
  const handleDetectQuery = useCallback(() => {
    if (!detectableQuery) return;
    setCopilotDetect((current) => ({ input: detectableQuery, nonce: (current?.nonce ?? 0) + 1 }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [detectableQuery]);

  const handleConsentContinue = useCallback((entry: CatalogEntry) => {
    setConsentEntry(null);
    setAdvancedConsentOpen(false);
    void executeConnect(entry);
  }, [executeConnect]);

  const renderConnectModals = () => (
    <>
      {consentEntry && (
        <ConnectConsentModal
          entry={consentEntry}
          busy={savingCatalogId === consentEntry.id}
          advancedOpen={advancedConsentOpen}
          permissionSelection={permissionSelection}
          onAdvancedToggle={() => setAdvancedConsentOpen((value) => !value)}
          onPermissionChange={(serviceId, level) => setPermissionSelection((previous) => ({ ...previous, [serviceId]: level }))}
          onCancel={() => {
            setConsentEntry(null);
            setAdvancedConsentOpen(false);
          }}
          onContinue={() => handleConsentContinue(consentEntry)}
        />
      )}
    </>
  );

  const handleTestIntegration = useCallback(async (entry: CatalogEntry) => {
    if (!account || !canManage) return;
    const requestSeq = integrationTestRequestSeq.current + 1;
    integrationTestRequestSeq.current = requestSeq;
    const isCurrent = () => mountedRef.current && integrationTestRequestSeq.current === requestSeq;
    setTestingCatalogId(entry.id);
    setError(null);
    setIntegrationTestResult(null);
    try {
      const response = await testWorkspaceIntegration(account.id, catalogEntryConnectionSlug(entry), workspaceId);
      if (isCurrent()) {
        setIntegrationTestResult(
          response.result ?? (response.reason ? { status: response.status, reason: response.reason } : {}),
        );
      }
    } catch (err: unknown) {
      if (!isCurrent()) return;
      const message = err instanceof Error ? err.message : `Failed to test ${entry.name} integration.`;
      setError(message);
    } finally {
      if (isCurrent()) {
        setTestingCatalogId(null);
      }
    }
  }, [account, canManage, workspaceId]);

  const handleRevoke = useCallback(async (entry: CatalogEntry) => {
    if (!account || !canManage) return;
    setRevokingCatalogId(entry.id);
    setError(null);
    setIntegrationTestResult(null);
    try {
      await revokeWorkspaceIntegration(account.id, catalogEntryConnectionSlug(entry), workspaceId);
      await load();
      if (mountedRef.current) {
        setNotice(`${entry.name} revoked.`);
        navigate(integrationPath(workspaceId), { replace: true });
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : `Failed to revoke ${entry.name}.`;
      setError(message);
    } finally {
      if (mountedRef.current) {
        setRevokingCatalogId(null);
      }
    }
  }, [account, canManage, load, navigate, workspaceId]);

  const handlePolicyStateChange = useCallback((toolName: string, state: WorkspaceIntegrationPolicyState) => {
    setPolicyDraft((previous) => updatePolicyDraftState(previous, toolName, state));
  }, []);

  const handleSavePolicy = useCallback(async () => {
    if (!account || !canManage || !selectedEntry || !selectedIntegration) return;
    setPolicySaving(true);
    setError(null);
    try {
      await updateWorkspaceIntegrationPolicy(account.id, selectedIntegrationSlug, {
        allowed_tools: policyDraft.allowedTools,
        denied_tools: policyDraft.deniedTools,
        ...(policyDraft.supportsAsk ? { policies: policyDraft.policies } : {}),
      }, workspaceId);
      await load();
      if (mountedRef.current) {
        setNotice("Policy saved.");
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : `Failed to update ${selectedEntry.name} policy.`;
      setError(message);
    } finally {
      if (mountedRef.current) {
        setPolicySaving(false);
      }
    }
  }, [account, canManage, load, policyDraft, selectedEntry, selectedIntegration, selectedIntegrationSlug, workspaceId]);

  if (!account) {
    return <p className="text-[13px] text-text-secondary">Loading account...</p>;
  }

  const renderHeader = (title: string, subtitle?: ReactNode) => (
    <div className="flex flex-col gap-3">
      <div>
        <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-text-tertiary">
          <span>{account.name}</span>
          {workspaceId && (
            <>
              <span>/</span>
              <Link to={`/workspaces/${workspaceId}`} className="hover:text-text-primary">
                {workspaceName}
              </Link>
            </>
          )}
        </div>
        <h1 className="mt-1 text-2xl font-bold text-text-primary md:text-3xl">{title}</h1>
        {subtitle}
      </div>
    </div>
  );

  const renderError = () => error && (
    <div role="alert" className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      <span>{error}</span>
    </div>
  );

  const renderNotice = () => !error && notice && (
    <div role="status" className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-500">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
      <span>{notice}</span>
    </div>
  );

  // Owner-facing approval inbox: agents block on require_approval tool calls
  // and can only reference a request id in chat, so pending requests must be
  // visible and actionable here.
  const renderApprovalInbox = () => approvalRequests.length > 0 && (
    <section className="rounded-[var(--radius-sm)] border border-amber-500/40 bg-amber-500/10 p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 flex-shrink-0 text-amber-600" />
        <h2 className="text-sm font-semibold text-amber-600">
          {approvalRequests.length === 1
            ? "1 tool call awaiting your approval"
            : `${approvalRequests.length} tool calls awaiting your approval`}
        </h2>
      </div>
      <div className="mt-3 space-y-2">
        {approvalRequests.map((request) => (
          <div
            key={request.id}
            className="flex flex-col gap-2 rounded-[var(--radius-sm)] border border-amber-500/20 bg-card px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 text-sm">
              <span className="font-medium text-text-primary">{request.tool_name}</span>
              <span className="text-text-tertiary"> · {request.integration_slug}</span>
              <div className="mt-0.5 text-xs text-text-tertiary">
                Requested {formatConnectionTime(request.created_at)} · expires {formatConnectionTime(request.expires_at)}
              </div>
            </div>
            {canManage && (
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleResolveApproval(request.id, "approve")}
                  disabled={actingApprovalId === request.id}
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {actingApprovalId === request.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => void handleResolveApproval(request.id, "deny")}
                  disabled={actingApprovalId === request.id}
                  className="inline-flex items-center rounded-[var(--radius-sm)] border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Deny
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );

  if (catalogId) {
    if (!selectedEntry) {
      return (
        <div className="space-y-6">
          {renderHeader("Integration not found")}
          <Link to={integrationPath(workspaceId)} className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary">
            <ArrowLeft className="h-4 w-4" />
            Back to catalog
          </Link>
        </div>
      );
    }

    const isCustomMCPEntry = catalogEntryIsCustomMCP(selectedEntry);
    // Generic custom-MCP plus curated remote-MCP OAuth entries (Notion, Jira, ...)
    // share the probe -> OAuth connect flow.
    const usesMCPConnectFlow = catalogEntryUsesMCPConnectFlow(selectedEntry);
    const isImportEntry = catalogEntryIsImport(selectedEntry);
    const previewTools = importPreviewTools(importPreview);
    const detailEntry: CatalogEntry = isCustomMCPEntry && customProbe
      ? {
        ...selectedEntry,
        name: customProbe.server.name || selectedEntry.name,
        developer: customProbe.server.name || selectedEntry.developer,
        description: `Remote MCP server with ${customProbe.tools.length} discovered ${customProbe.tools.length === 1 ? "tool" : "tools"}.`,
        usageSuggestion: "Review the discovered tools, then connect this server for the workspace.",
        tools: customMCPToolsFromProbe(customProbe.tools),
      }
      : isImportEntry && importPreview
        ? {
          ...selectedEntry,
          name: importPreview.display_name || selectedEntry.name,
          description: `Imported ${importPreview.type === "graphql" ? "GraphQL schema" : "OpenAPI spec"} with ${importPreview.tools.length} generated ${importPreview.tools.length === 1 ? "tool" : "tools"}.`,
          usageSuggestion: "Review generated tools, access classification, and policy before saving this workspace connection.",
          tools: previewTools,
        }
      : selectedEntry;
    const Icon = detailEntry.Icon;
    const isConnected = selectedIntegrationConnected;
    const isBusy = savingCatalogId === selectedEntry.id;
    const isRevoking = revokingCatalogId === selectedEntry.id;
    const connectionDisabled = catalogEntryConnectionDisabled(selectedEntry);
    const detailAuthLabel = isImportEntry && importPreview
      ? workspaceIntegrationImportAuthLabel(importPreview.auth_kind)
      : detailEntry.authKind;
    // OAuth servers return 0 tools before auth, so allow submit once a probe found
    // either tools (bearer) or usable OAuth metadata.
    const canSubmitConnection = !connectionDisabled
      && requiredCatalogFieldsPresent(selectedEntry, fieldValues)
      && (!usesMCPConnectFlow || Boolean(customProbe?.tools.length) || Boolean(customProbe?.oauth?.available))
      && (!isImportEntry || Boolean(importPreview?.tools.length));
    const snapshotIntegration = selectedIntegration?.kind === "custom-mcp" ? selectedIntegration : null;
    const snapshotStatus = snapshotIntegration ? workspaceIntegrationSnapshotStatus(snapshotIntegration) : null;
    const detailEndpoint = selectedIntegration?.target
      || importPreview?.endpoint
      || detailEntry.endpoint
      || (isCustomMCPEntry ? catalogFieldValue(fieldValues, "url") : "")
      || (isImportEntry ? (catalogFieldValue(fieldValues, "endpoint") || catalogFieldValue(fieldValues, "base_url") || catalogFieldValue(fieldValues, "spec_url")) : "");
    // Once connected, show the REAL discovered tools from the integration manifest
    // (not the catalog's illustrative placeholders) so the policy table lets the
    // admin enable/disable every actual tool by its real name.
    const detailTools = (selectedIntegration?.tools && selectedIntegration.tools.length > 0)
      ? selectedIntegration.tools
      : isImportEntry && importPreview
        ? previewTools
      : detailEntry.tools;
    const approvalLabel = selectedIntegration?.approved_at && selectedIntegration.approved_by_user_id
      ? `Approved by ${memberDisplayName(members, selectedIntegration.approved_by_user_id)}`
      : "Approved by your admin";
    const googleServiceStatuses = googleServiceStatusItems(selectedIntegration);

    return (
      <div className="space-y-6">
        {renderConnectModals()}
        {renderHeader(detailEntry.name, (
          <p className="mt-2 max-w-2xl text-sm text-text-secondary">{detailEntry.description}</p>
        ))}
        {renderError()}
        {renderNotice()}
        {renderApprovalInbox()}

        <Link to={integrationPath(workspaceId)} className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary">
          <ArrowLeft className="h-4 w-4" />
          Back to catalog
        </Link>

        <section className="rounded-[var(--radius-sm)] border border-border bg-card p-4 shadow-card">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-surface)]">
                <Icon className="h-5 w-5 text-text-primary" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-text-primary">{detailEntry.name}</h2>
                  <ConnectorStatus connected={isConnected} />
                  <ApprovalBadge label={approvalLabel} />
                </div>
                <p className="mt-2 text-sm text-text-secondary">{detailEntry.usageSuggestion}</p>
              </div>
            </div>
            <Link
              to="/chat"
              className="inline-flex items-center justify-center rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-[var(--color-surface-hover)]"
            >
              Try in chat
            </Link>
          </div>
        </section>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
          <section className="rounded-[var(--radius-sm)] border border-border bg-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Connection</h2>
              <span className="text-xs text-text-tertiary">{detailAuthLabel}</span>
            </div>

            {selectedIntegration?.target && (
              <div className="mt-4 rounded-[var(--radius-sm)] border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-500">
                Connected as {selectedIntegration.target}
              </div>
            )}

            {selectedIntegration && (
              <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] px-3 py-2 text-xs text-text-secondary">
                <div>
                  Connected by {memberDisplayName(members, selectedIntegration.connected_by_user_id)}
                </div>
                <div className="mt-0.5">
                  {formatConnectionTime(selectedIntegration.connected_at)}
                </div>
              </div>
            )}

            <GoogleServiceStatusPanel statuses={googleServiceStatuses} />

            {snapshotIntegration && snapshotStatus && (
              <div className={`mt-4 rounded-[var(--radius-sm)] border px-3 py-2 text-xs ${
                snapshotStatus.state === "stale"
                  ? "border-amber-500/30 bg-amber-500/10 text-amber-500"
                  : snapshotStatus.state === "fresh"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                    : "border-border bg-[var(--color-surface)] text-text-secondary"
              }`}>
                <div className="font-medium">Tool snapshot: {snapshotStatus.label}</div>
                {snapshotIntegration.snapshot?.probed_at && (
                  <div className="mt-0.5">Last probed {formatConnectionTime(snapshotIntegration.snapshot.probed_at)}</div>
                )}
                {snapshotIntegration.snapshot?.server_name && (
                  <div className="mt-0.5">
                    {snapshotIntegration.snapshot.server_name}
                    {snapshotIntegration.snapshot.server_version ? ` ${snapshotIntegration.snapshot.server_version}` : ""}
                  </div>
                )}
              </div>
            )}

            {detailEntry.fields && (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {detailEntry.fields.map((field) => (
                  <label key={field.id} className={field.id === "token" || field.type === "textarea" ? "block md:col-span-2" : "block"}>
                    <span className="mb-1 block text-[11px] uppercase tracking-wider text-text-tertiary">{field.label}</span>
                    {field.type === "textarea" ? (
                      <textarea
                        value={fieldValues[field.id] ?? ""}
                        onChange={(event) => handleFieldValueChange(field.id, event.target.value)}
                        placeholder={field.placeholder}
                        rows={8}
                        className="w-full rounded-[var(--radius-sm)] border border-border bg-input px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-500"
                      />
                    ) : field.type === "select" ? (
                      <select
                        value={fieldValues[field.id] ?? ""}
                        onChange={(event) => handleFieldValueChange(field.id, event.target.value)}
                        className="w-full rounded-[var(--radius-sm)] border border-border bg-input px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-500"
                      >
                        <option value="">{field.placeholder}</option>
                        {(field.options ?? []).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        value={fieldValues[field.id] ?? ""}
                        onChange={(event) => handleFieldValueChange(field.id, event.target.value)}
                        placeholder={field.id === "token" && isConnected ? "Leave blank to keep existing token" : field.placeholder}
                        className="w-full rounded-[var(--radius-sm)] border border-border bg-input px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-500"
                      />
                    )}
                  </label>
                ))}
              </div>
            )}

            {usesMCPConnectFlow && (
              <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">{isConnected ? "Refresh snapshot" : "Probe first"}</h3>
                    <p className="mt-1 text-xs text-text-secondary">
                      {isConnected
                        ? "Re-probe before changing the enabled tool set or refreshing this server."
                        : "OCM checks the server before saving it. Nothing is connected until you continue."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleProbeCustomMCP}
                    disabled={probingCustomMCP || !(catalogFieldValue(fieldValues, "url") || selectedEntry.endpoint)}
                    className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-[var(--color-surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {probingCustomMCP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    {isConnected ? "Re-probe server" : "Probe server"}
                  </button>
                </div>
                {customProbe && (
                  <div className="mt-3 grid gap-2 border-t border-border pt-3 text-xs text-text-secondary sm:grid-cols-3">
                    <InfoRow label="Server" value={customProbe.server.name || "Remote MCP"} />
                    <InfoRow label="Protocol" value={customProbe.protocol_version || "Unknown"} />
                    <InfoRow label="Discovered" value={`${customProbe.tools.length} tools`} />
                  </div>
                )}
                {customProbe?.oauth?.available && (
                  <div className="mt-3 rounded-[var(--radius-sm)] border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                    <div className="font-medium">OAuth metadata detected</div>
                    <div className="mt-0.5">
                      {customProbe.oauth.issuer || customProbe.oauth.authorization_server || "Authorization server advertised"}
                      {customProbe.oauth.dynamic_client_registration ? " · dynamic registration available" : ""}
                    </div>
                    {customProbe.oauth.scopes_supported?.length ? (
                      <div className="mt-3 space-y-2 text-amber-600">
                        <div className="font-medium">OAuth scopes</div>
                        <div className="grid gap-1 sm:grid-cols-2">
                          {customProbe.oauth.scopes_supported.map((scope) => (
                            <label key={scope} className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-amber-500/20 bg-card/60 px-2 py-1">
                              <input
                                type="checkbox"
                                checked={customOAuthScopes.includes(scope)}
                                onChange={(event) => {
                                  setCustomOAuthScopes((previous) => (
                                    event.target.checked
                                      ? [...previous, scope]
                                      : previous.filter((candidate) => candidate !== scope)
                                  ));
                                }}
                              />
                              <span className="break-all">{scope}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {!customProbe.oauth.dynamic_client_registration && (
                      <label className="mt-3 block text-amber-600">
                        <span className="font-medium">OAuth client ID</span>
                        <input
                          type="text"
                          value={customOAuthClientId}
                          onChange={(event) => setCustomOAuthClientId(event.target.value)}
                          placeholder="Pre-registered public client id"
                          className="mt-1 w-full rounded-[var(--radius-sm)] border border-amber-500/30 bg-card px-2 py-1.5 text-xs text-text-primary outline-none transition-colors placeholder:text-text-tertiary focus:border-amber-500"
                        />
                      </label>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCustomMCPOAuthContinue}
                        disabled={savingCatalogId === selectedEntry.id || (!customProbe.oauth.dynamic_client_registration && !customOAuthClientId.trim())}
                        className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] border border-amber-500/40 bg-card px-3 py-2 text-xs font-medium text-amber-600 transition-colors hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingCatalogId === selectedEntry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                        Continue with OAuth
                      </button>
                      <span className="text-amber-600/80">Bearer/API-key remains available.</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isImportEntry && (
              <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">{importPreview ? "Preview ready" : "Preview import"}</h3>
                    <p className="mt-1 text-xs text-text-secondary">
                      OCM generates fixed tools from the spec before anything is saved.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handlePreviewImport}
                    disabled={previewingImport || !requiredCatalogFieldsPresent(selectedEntry, fieldValues)}
                    className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-[var(--color-surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {previewingImport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    Preview tools
                  </button>
                </div>
                {importPreview && (
                  <div className="mt-3 grid gap-2 border-t border-border pt-3 text-xs text-text-secondary sm:grid-cols-4">
                    <InfoRow label="Import" value={importPreview.type === "graphql" ? "GraphQL" : "OpenAPI"} />
                    <InfoRow label="Auth" value={workspaceIntegrationImportAuthLabel(importPreview.auth_kind)} />
                    <InfoRow label="Endpoint" value={importPreview.endpoint} />
                    <InfoRow label="Generated" value={`${importPreview.tools.length} tools`} />
                  </div>
                )}
                {importPreview?.denied_tools?.length ? (
                  <div className="mt-3 rounded-[var(--radius-sm)] border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
                    {importPreview.denied_tools.length} generated write {importPreview.denied_tools.length === 1 ? "tool is" : "tools are"} blocked by default.
                  </div>
                ) : null}
              </div>
            )}

            {detailEntry.authKind === "oauth" && !selectedIntegration?.target && (
              <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] px-3 py-2 text-xs text-text-secondary">
                Uses provider OAuth with offline refresh for workspace-scoped tools.
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    if (isImportEntry) {
                      void executeConnect(detailEntry);
                      return;
                    }
                    // Probed an OAuth server (with dynamic registration) and no
                    // bearer token entered → hand off to the provider OAuth flow
                    // instead of a bearer save.
                    if (usesMCPConnectFlow && customProbe?.oauth?.available && (customProbe.oauth.dynamic_client_registration || customOAuthClientId.trim()) && !catalogSecret(detailEntry, fieldValues)) {
                      void handleCustomMCPOAuthContinue();
                      return;
                    }
                    handleConnect(detailEntry);
                  }}
                  disabled={isBusy || !canSubmitConnection}
                  className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : detailEntry.authKind === "oauth" ? <Plug className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                  {isImportEntry ? "Save import" : isConnected ? `Reconnect ${detailEntry.name}` : `Connect ${detailEntry.name}`}
                </button>
              )}
              {canManage && isConnected && (
                <button
                  type="button"
                  onClick={() => handleTestIntegration(detailEntry)}
                  disabled={testingCatalogId === selectedEntry.id}
                  className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-[var(--color-surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {testingCatalogId === selectedEntry.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                  Test connection
                </button>
              )}
              {canManage && isConnected && (
                <button
                  type="button"
                  onClick={() => handleRevoke(detailEntry)}
                  disabled={isRevoking}
                  className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] border border-red-500/30 px-3 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                  Revoke
                </button>
              )}
            </div>

            {integrationTestResult && (
              <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] px-3 py-2 text-sm text-text-primary">
                Last test: {String(integrationTestResult.full_name || integrationTestResult.emailAddress || `${detailEntry.name} reachable`)}
              </div>
            )}
          </section>

          <section className="rounded-[var(--radius-sm)] border border-border bg-card p-4 shadow-card">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Information</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <InfoRow label="Capabilities" value={`${detailTools.length} tools`} />
              <InfoRow label="Developer" value={detailEntry.developer} />
              <InfoRow label="Category" value={detailEntry.category} />
              <InfoRow label="Website" value={detailEntry.website} />
              <InfoRow label="Privacy" value={detailEntry.privacy} />
              <InfoRow label="Terms" value={detailEntry.terms} />
            </dl>
            <details className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] px-3 py-2 text-sm text-text-secondary">
              <summary className="cursor-pointer text-sm font-medium text-text-primary">Advanced</summary>
              <dl className="mt-3 space-y-3 border-t border-border pt-3">
                <InfoRow label="Transport" value={detailEntry.transport} />
                <InfoRow label="Auth" value={detailAuthLabel} />
                <InfoRow label="Endpoint" value={detailEndpoint || "Provider managed"} />
                {isCustomMCPEntry && (
                  <InfoRow label="Resources/prompts" value="Not connected in v1" />
                )}
                {selectedIntegration?.scopes?.length ? (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-text-tertiary">Raw scopes</dt>
                    <dd className="mt-1 break-words text-xs text-text-secondary">{selectedIntegration.scopes.join(", ")}</dd>
                  </div>
                ) : null}
              </dl>
            </details>
          </section>
        </div>

        <section className="rounded-[var(--radius-sm)] border border-border bg-card p-4 shadow-card">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Tools</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {detailEntry.tools.length === 0 && (
              <div className="rounded-[var(--radius-sm)] border border-dashed border-border bg-[var(--color-surface)] p-4 text-sm text-text-tertiary">
                {isImportEntry ? "Preview the import to review generated tools before saving." : "Probe the server to review tools before connecting."}
              </div>
            )}
            {detailTools.map((tool) => (
              <div key={tool.name} className="rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-medium text-text-primary">{tool.name}</h3>
                    <p className="mt-1 text-xs text-text-secondary">{tool.description}</p>
                  </div>
                  <div className="flex flex-shrink-0 flex-wrap justify-end gap-1">
                    {tool.source && <ScopePill>{tool.source}</ScopePill>}
                    <ScopePill>{tool.access}</ScopePill>
                    <ScopePill>{tool.mode}</ScopePill>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {canManage && (selectedIntegration || (isImportEntry && importPreview)) && (
            <div className="mt-4 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] p-3">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-semibold text-text-primary">Policy</h3>
                {selectedIntegration && (
                  <button
                    type="button"
                    onClick={handleSavePolicy}
                    disabled={policySaving}
                    className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {policySaving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save policy
                  </button>
                )}
              </div>
              <p className="mb-3 text-xs text-text-tertiary">
                {policyDraft.supportsAsk
                  ? "Ask requires approval before the tool runs; Deny blocks it entirely."
                  : "Deny always wins. With no Allow entries every tool is allowed; adding any Allow entry restricts the integration to just the allowed tools."}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-left">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-text-tertiary">
                      <th className="pb-2 pr-4 font-medium">Tool</th>
                      <th className="pb-2 pr-4 font-medium">Allow</th>
                      {policyDraft.supportsAsk && <th className="pb-2 pr-4 font-medium">Ask</th>}
                      <th className="pb-2 pr-4 font-medium">Deny</th>
                      <th className="pb-2 font-medium">Effective</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailTools.map((tool) => {
                      const state = policyStateForTool(policyDraft, tool.name);
                      const effective = policyLabelForState(state);
                      const inputName = `policy-${tool.name}`;
                      return (
                        <tr key={tool.name} className="border-t border-border">
                          <td className="py-2 pr-4 text-sm text-text-primary">{tool.name}</td>
                          <td className="py-2 pr-4">
                            <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
                              <input
                                type="radio"
                                name={inputName}
                                checked={state === "allow"}
                                onChange={() => handlePolicyStateChange(tool.name, "allow")}
                                className="h-4 w-4 border-border bg-input"
                              />
                              Allow
                            </label>
                          </td>
                          {policyDraft.supportsAsk && (
                            <td className="py-2 pr-4">
                              <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
                                <input
                                  type="radio"
                                  name={inputName}
                                  checked={state === "require_approval"}
                                  onChange={() => handlePolicyStateChange(tool.name, "require_approval")}
                                  className="h-4 w-4 border-border bg-input"
                                />
                                Ask
                              </label>
                            </td>
                          )}
                          <td className="py-2 pr-4">
                            <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
                              <input
                                type="radio"
                                name={inputName}
                                checked={state === "block"}
                                onChange={() => handlePolicyStateChange(tool.name, "block")}
                                className="h-4 w-4 border-border bg-input"
                              />
                              Deny
                            </label>
                          </td>
                          <td className="py-2">
                            <span className={`text-xs font-medium ${effective === "Blocked" ? "text-red-500" : effective === "Ask" ? "text-amber-500" : "text-emerald-500"}`}>
                              {effective}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <MachineRuntimeSection
          loading={loading}
          machines={machines}
          hasConfiguredIntegration={hasConfiguredIntegration}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {renderConnectModals()}
      {renderHeader("Workspace Integrations", (
        <p className="mt-2 max-w-2xl text-sm text-text-secondary">
          Connect apps once. Machines in {workspaceName} inherit them.
        </p>
      ))}
      {renderError()}
      {renderNotice()}
      {renderApprovalInbox()}

      {canManage && (
        <CopilotPanel
          accountId={account.id}
          workspaceId={workspaceId}
          onSaved={load}
          quickAdds={copilotQuickAdds}
          onOAuthConnect={handleCopilotOAuthConnect}
          onOpenDetail={handleCopilotOpenDetail}
          externalDetect={copilotDetect}
        />
      )}

      {addedEntries.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-text-primary">Added</h2>
            <span className="text-xs text-text-tertiary">{addedEntries.length} connected</span>
          </div>
          <div className="divide-y divide-border rounded-[var(--radius-sm)] border border-border bg-card px-4 shadow-card">
            {addedEntries.map(({ integration, entry }) => {
              if (!entry) {
                return (
                  <Link
                    key={integration.id}
                    to={integrationPath(workspaceId, integration.slug)}
                    className="flex items-center justify-between gap-3 py-4 text-sm text-text-primary hover:text-brand-600"
                  >
                    <span className="flex items-center gap-2">
                      {integration.display_name}
                      <VerifiedChip lastTest={integration.last_test} />
                    </span>
                    <span className="text-xs text-text-tertiary">Manage</span>
                  </Link>
                );
              }
              return (
                <IntegrationCatalogRow
                  key={`${integration.id}:${entry.id}`}
                  entry={entry}
                  connected
                  canManage={canManage}
                  href={integrationPath(workspaceId, entry.id)}
                  onConnect={() => handleConnect(entry)}
                  lastTest={integration.last_test}
                />
              );
            })}
          </div>
        </div>
      )}

      {workspaceId && (
        <section className="rounded-[var(--radius-sm)] border border-border bg-card p-4 shadow-card">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Integration health</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Last 24 hours across workspace tools.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <ScopePill>{healthTools.length} active tools</ScopePill>
              <ScopePill>{unhealthyToolCount} need attention</ScopePill>
            </div>
          </div>

          {healthTools.length === 0 ? (
            <div className="mt-4 rounded-[var(--radius-sm)] border border-dashed border-border px-4 py-6 text-sm text-text-tertiary">
              No integration tool calls recorded in this window.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[680px] text-left">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-text-tertiary">
                    <th className="pb-2 pr-4 font-medium">Tool</th>
                    <th className="pb-2 pr-4 font-medium">Calls</th>
                    <th className="pb-2 pr-4 font-medium">Success</th>
                    <th className="pb-2 pr-4 font-medium">P95</th>
                    <th className="pb-2 pr-4 font-medium">Retries</th>
                    <th className="pb-2 font-medium">Top failure</th>
                  </tr>
                </thead>
                <tbody>
                  {healthTools.slice(0, 8).map((tool) => {
                    const topFailure = tool.top_failure_classes?.[0];
                    return (
                      <tr key={tool.tool_id} className="border-t border-border">
                        <td className="py-2 pr-4">
                          <div className="text-sm font-medium text-text-primary">{tool.tool_id}</div>
                          <div className="text-xs text-text-tertiary">{tool.transport} · {tool.access}</div>
                        </td>
                        <td className="py-2 pr-4 text-sm text-text-secondary">{tool.total_calls}</td>
                        <td className="py-2 pr-4 text-sm text-text-secondary">{formatPercent(tool.success_rate)}</td>
                        <td className="py-2 pr-4 text-sm text-text-secondary">{formatLatency(tool.p95_latency_ms)}</td>
                        <td className="py-2 pr-4 text-sm text-text-secondary">{tool.avg_retry_count.toFixed(2)}</td>
                        <td className="py-2 text-sm text-text-secondary">
                          {topFailure ? `${topFailure.class} (${topFailure.count})` : "None"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* The chat is the primary add surface; the full catalog stays one click
          away as a collapsed browser (and the classic-form escape hatch). The
          chat's "Browse all" / "Use classic form" links expand it via the
          #integration-catalog hash. */}
      <details
        id="integration-catalog"
        open={catalogOpen}
        onToggle={(event) => setCatalogOpen((event.target as HTMLDetailsElement).open)}
        className="group"
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-[var(--radius-sm)] border border-border bg-card px-4 py-3 text-sm text-text-secondary shadow-card hover:bg-[var(--color-surface-hover)]">
          <span className="font-medium text-text-primary">Browse all integrations</span>
          <span className="text-xs text-text-tertiary">
            {catalogEntries.length} apps · classic forms
          </span>
        </summary>
        <div className="mt-3 space-y-4">
        <div className="relative max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <input
              ref={catalogSearchRef}
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search integrations"
              className="w-full rounded-[var(--radius-sm)] border border-border bg-input py-2 pl-9 pr-12 text-sm text-text-primary outline-none focus:border-brand-500"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border bg-elevated px-1.5 text-[11px] text-text-tertiary">
              /
            </kbd>
        </div>

        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by format">
          {(["all", "prebuilt", "mcp", "api"] as const).map((format) => {
            const count =
              format === "all"
                ? searchMatchedCatalog.length
                : catalogFormatCounts[format];
            const active = catalogFormat === format;
            return (
              <button
                key={format}
                type="button"
                aria-pressed={active}
                onClick={() => setCatalogFormat(format)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  active
                    ? "border-brand-500 bg-brand-500/10 text-text-primary"
                    : "border-border text-text-secondary hover:text-text-primary"
                }`}
              >
                {format === "all" ? "All" : CATALOG_FORMAT_LABELS[format]}
                <span className="ml-1.5 text-text-tertiary">{count}</span>
              </button>
            );
          })}
        </div>

        {catalogByCategory.length === 0 ? (
          detectableQuery && canManage ? (
            <button
              type="button"
              onClick={handleDetectQuery}
              className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-sm)] border border-dashed border-brand-500/50 bg-brand-500/5 px-4 py-4 text-left text-sm hover:border-brand-500"
            >
              <span className="text-text-primary">
                <span className="font-mono">{detectableQuery}</span> isn&apos;t in the catalog yet
              </span>
              <span className="flex-shrink-0 font-medium text-brand-500">Detect it with the copilot →</span>
            </button>
          ) : (
            <div className="rounded-[var(--radius-sm)] border border-dashed border-border px-4 py-8 text-center text-sm text-text-tertiary">
              No integrations match the current filters.
            </div>
          )
        ) : (
          catalogByCategory.map(([category, entries]) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-text-primary">{category}</h2>
                <span className="text-xs text-text-tertiary">{entries.length} {entries.length === 1 ? "app" : "apps"}</span>
              </div>
              <div className="rounded-[var(--radius-sm)] border border-border bg-card px-4 shadow-card">
                {entries.map((entry) => {
                  const integration = integrationBySlug.get(catalogEntryConnectionSlug(entry));
                  const connected = isWorkspaceIntegrationConnected(integration);
                  const disabled = !connected && catalogEntryConnectionDisabled(entry);
                  return (
                    <IntegrationCatalogRow
                      key={entry.id}
                      entry={entry}
                      connected={connected}
                      canManage={canManage}
                      disabled={disabled}
                      href={integrationPath(workspaceId, entry.id)}
                      onConnect={() => handleConnect(entry)}
                    />
                  );
                })}
              </div>
            </div>
          ))
        )}
        </div>
      </details>

      <details className="group">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-[var(--radius-sm)] border border-border bg-card px-4 py-3 text-sm text-text-secondary shadow-card hover:bg-[var(--color-surface-hover)]">
          <span>Workspace status</span>
          <span className="text-xs text-text-tertiary">
            {configuredMachineCount}/{machines.length} machines configured
          </span>
        </summary>
        <div className="mt-3">
          <MachineRuntimeSection
            loading={loading}
            machines={machines}
            hasConfiguredIntegration={hasConfiguredIntegration}
          />
        </div>
      </details>

      <ExternalAgentsCard accountId={account.id} workspaceId={workspaceId} canManage={canManage} />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-right font-medium text-text-primary">{value}</dd>
    </div>
  );
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value * 100)}%`;
}

function formatLatency(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0ms";
  if (value >= 1000) return `${(value / 1000).toFixed(1)}s`;
  return `${Math.round(value)}ms`;
}

function MachineRuntimeSection({
  loading,
  machines,
  hasConfiguredIntegration,
}: {
  loading: boolean;
  machines: WorkspaceIntegrationMachineConsumer[];
  hasConfiguredIntegration: boolean;
}) {
  return (
    <section className="rounded-[var(--radius-sm)] border border-border bg-card p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Machine assignment</h2>
        <Plug className="h-4 w-4 text-text-tertiary" />
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      ) : machines.length === 0 ? (
        <p className="py-4 text-sm text-text-tertiary">No machines in this workspace.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-text-tertiary">
                <th className="pb-2 pr-4 font-medium">Machine</th>
                <th className="pb-2 pr-4 font-medium">Status</th>
                <th className="pb-2 pr-4 font-medium">Config</th>
                <th className="pb-2 font-medium text-right">Assignment</th>
              </tr>
            </thead>
            <tbody>
              {machines.map((machine) => (
                <MachineRuntimeRow
                  key={machine.machine_id}
                  machine={machine}
                  hasConfiguredIntegration={hasConfiguredIntegration}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export {
  CATALOG,
  CATALOG_SEARCH_ALIASES,
  catalogConfig,
  catalogEntriesFromBackend,
  catalogIconForSlug,
  catalogSearchMatches,
  catalogSecret,
  customMCPInitialPolicy,
  customMCPManifestFromProbe,
  customMCPToolsFromProbe,
  deriveCustomMCPSlug,
  effectivePolicy,
  googleWorkspaceOAuthPayload,
  requiredCatalogFieldsPresent,
  workspaceIntegrationSnapshotStatus,
} from "./workspaceIntegrationsCatalog";
